/**
 * NP-505 — keeps the DBA accent scheme on <html> in step with the branch context.
 *
 * Renders nothing. Mounted above the routes so every page is themed (AC6), and
 * it re-resolves as context arrives: the entry `?lo=` colours login/registration
 * before any loan exists (AC2), and a loaded loan's own officer takes over once
 * known (AC1) — the loan is the authority on its branch, the captured link is
 * only the best guess until one loads.
 *
 * QA follow-up: "it takes a while before the new color scheme updates". The officer
 * used to be read off the ten-section composite, so the page sat in the previous
 * branch's colours until the slowest section returned — measured at 6–8s, repainting
 * on the same frame as the loan figures. Two changes close that: the officer now
 * comes from a contacts-only read, and the last officer each loan resolved to is
 * remembered, so returning to a loan themes on the first paint with no request at all.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLoan } from '@/context/LoanContext';
import { useLoanOfficerContacts } from '@/hooks/useLoanQuery';
import { findLoanOfficerEmail } from '@/lib/loEmail';
import {
  applyDbaTheme,
  entryLoanOfficerEmail,
  recalledLoanOfficerEmail,
  rememberLoanOfficerEmail,
  resolveDbaThemeId,
} from '@/lib/dbaTheme';

export function DbaThemeSync() {
  const [searchParams] = useSearchParams();
  const { loanId } = useLoan();
  const { data: contacts } = useLoanOfficerContacts(loanId);

  const search = searchParams.toString();
  const liveEmail = findLoanOfficerEmail(contacts?.contacts);

  // Precedence is deliberate. The live officer wins whenever it is known, INCLUDING
  // when it maps to no DBA — a loan handled by an AFN officer must fall back to the
  // PATH scheme, not inherit the branch that was on screen a moment ago. Only while
  // the loan's officer is genuinely unknown do the remembered and entry values apply.
  const email =
    liveEmail ?? recalledLoanOfficerEmail(loanId) ?? entryLoanOfficerEmail(search);

  useEffect(() => {
    applyDbaTheme(resolveDbaThemeId(email));
  }, [email]);

  useEffect(() => {
    if (loanId && liveEmail) rememberLoanOfficerEmail(loanId, liveEmail);
  }, [loanId, liveEmail]);

  return null;
}
